import React, { useMemo, useState } from "react";
import { Button, Divider, Form, Grid, Segment } from "semantic-ui-react";
import {
    DateRangePowerSelector,
    DateTimeUtils,
    ModulesFilter,
    PowerSelector,
} from "dyl-components";
import {
    KEY_CONTACT_ACCOUNT,
    KEY_DATE,
    KEY_PHONE_NUMBER,
    KEY_USER,
    KEY_DURATION,
    KEY_ACTIONS,
    KEY_DIRECTION,
    KEY_SYSTEM_RESULT,
    KEY_NOTE,
    KEY_USER_RESULT,
    KEY_DESTINATION,
    KEY_CALL_TAGS,
    KEY_CALL_TYPE,
} from "shared/constants/CALL_HISTORY_LOGS_COLUMNS";
import { useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { PhoneUtil } from "utils";
import { FormProvider, useForm } from "react-hook-form";
import { generateResolver, yup } from "dyl-components/atoms/ValidationUtils";
import {
    getDataNonQuery,
    useRangeCalendar,
} from "shared/CustomDateRange/customDateRange";
import CustomRangeDateModal from "shared/modals/CustomRangeDateModal";

const COLUMN_ROWS = [
    [
        { value: KEY_DATE, label: "Date", isForcedChecked: true },
        {
            value: KEY_CONTACT_ACCOUNT,
            label: "Contact/Account",
            isForcedChecked: true,
        },
        {
            value: KEY_PHONE_NUMBER,
            label: "Phone Number",
            isForcedChecked: true,
        },
        {
            value: KEY_USER,
            label: "User",
            isForcedChecked: true,
        },
        {
            value: KEY_DIRECTION,
            label: "Direction",
        },
        {
            value: KEY_CALL_TYPE,
            label: "Call Type",
        },
        {
            value: KEY_SYSTEM_RESULT,
            label: "System Result",
        },
        {
            value: KEY_USER_RESULT,
            label: "User Result",
        },
        {
            value: KEY_DURATION,
            label: "Duration",
            isForcedChecked: true,
        },
        {
            value: KEY_DESTINATION,
            label: "Destination",
            isForcedChecked: true,
        },
        {
            value: KEY_CALL_TAGS,
            label: "Call Tags",
        },
        {
            value: KEY_NOTE,
            label: "Note",
        },
        {
            value: KEY_ACTIONS,
            label: "Actions",
            isForcedChecked: true,
        },
    ],
];

const CallHistoryToolbar = ({ isColumnHidden, onToggleColumn }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [params, setSearchParams] = useSearchParams();
    const [searchValue, setSearchValue] = useState(
        params.get("search") || params.get("phone") || ""
    );

    const {
        destinations,
        users,
        isCallHistoryLoading,
        userAuthTimezone,
        listOfTagCategories,
    } = useSelector((state) => {
        return {
            destinations: state?.pbx_config?.destinations,
            users: state.users.bulkUsernames,
            isCallHistoryLoading: state?.call_history?.isReadingCallHistory,
            userAuthTimezone: state.auth.timezone,
            listOfTagCategories: state?.tag_categories?.listOfTagCategories,
        };
    });
    const {
        dateRange,
        setCustomDateRangeOpen,
        isCustomDateRangeOpen,
        setDateRange,
    } = useRangeCalendar(params, false);

    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            filters: {
                destination: [],
                system_result:
                    params?.get?.("system_result")?.split?.(",") ?? [],
                user_result: params?.get?.("user_result")?.split?.(",") ?? [],
                direction: params?.get?.("direction")?.split?.(",") ?? [],
                user_id: params?.get?.("user_id")?.split?.(",") ?? [],
                voicemail_listened:
                    params?.get?.("voicemail_listened")?.split?.(",") ?? [],
                date: "",
                tags: params?.get?.("tags")?.split?.(",") ?? [],
                call_type: params?.get?.("call_type")?.split?.(", ") ?? [],
            },
        },
        resolver: generateResolver({
            filters: yup
                .object()
                .test(
                    "should_have_at_least_one_filter",
                    "At least one filter should be active",
                    (filters) => {
                        return Object.keys(filters).length > 0;
                    }
                ),
        }),
    });

    const {
        formState: { isValid, isDirty },
    } = methods;

    const userOptions = useMemo(() => {
        if (!users?.length) return [];
        return users?.map?.((user) => ({
            text: `${user?.first_name || ""} ${user?.last_name || ""}`,
            value: user?.id,
            key: user?.id,
        }));
    }, [users]);

    const destinationsOptions = useMemo(() => {
        if (!destinations) return;
        return Object.keys(destinations)
            ?.map?.((destination) => {
                const label =
                    PhoneUtil.DESTINATION_TYPES.find(
                        (type) => type?.value === destination
                    )?.text || destination;
                return {
                    label,
                    value: destination,
                    key: destination,
                    children: destinations?.[destination]?.map?.((child) => ({
                        label: child.label,
                        value: child?.fs_destination,
                        key: child?.destination_id,
                    })),
                };
            })
            ?.filter((destination) => destination?.children?.length > 0);
    }, [destinations]);

    const tagsCategoryOptions = useMemo(() => {
        if (!listOfTagCategories?.length) return [];
        return listOfTagCategories?.map?.((category) => {
            return {
                label: category?.category,
                value: category?.id,
                key: category?.id,
                children: category?.tags?.map((tag) => ({
                    label: String(tag?.tag),
                    value: String(tag?.id),
                    key: tag?.id,
                })),
            };
        });
    }, [listOfTagCategories]);

    const onSearchSubmit = (value) => {
        try {
            const query = new URLSearchParams(params);
            query.delete("phone");
            query.delete("search");
            const searchKey = PhoneUtil.isPhoneNumber(value)
                ? "phone"
                : "search";
            query.set(searchKey, value.trim());
            const query_string = query.toString();
            navigate(
                `${location.pathname}${query_string ? `?${query_string}` : ""}`
            );
        } catch (e) {
            console.log(e);
        }
    };

    const onSearch = (event) => {
        setSearchValue(event?.target?.value);
    };

    const cancelFunction = () => {
        setSearchValue("");
        const query = new URLSearchParams(params);
        query.delete("phone");
        query.delete("search");
        const query_string = query.toString();
        navigate(
            `${location.pathname}${query_string ? `?${query_string}` : ""}`
        );
    };

    const onSubmit = methods.handleSubmit((data) => {
        const filters = { ...data?.filters };

        if (filters?.date) {
            if (filters.date === "all_time") {
                delete filters.start_at;
                delete filters.end_at;
                params.delete("start_at");
                params.delete("end_at");
            } else if (filters.date === "custom") {
                const timestampRange = getDataNonQuery(
                    setCustomDateRangeOpen,
                    dateRange
                );
                if (timestampRange.start && timestampRange.end) {
                    filters.start_at = timestampRange.start;
                    filters.end_at = timestampRange.end;
                }
            } else {
                const { start, end } = DateTimeUtils.getDateRange(
                    filters.date,
                    DateTimeUtils.getTimezoneCurrentDay(userAuthTimezone),
                    DateTimeUtils.DATE_FORMAT,
                    "day",
                    true,
                    userAuthTimezone
                );
                filters.start_at = start;
                filters.end_at = end;
            }
            delete filters.date;
        }

        params.delete("page");

        const existingFiltersObj = Object.fromEntries(params.entries());
        const mergedFilters = { ...existingFiltersObj, ...filters };

        const apiFilters = new URLSearchParams();
        Object.entries(mergedFilters).forEach(([key, value]) => {
            if (Array.isArray(value) && value?.length) {
                apiFilters.set(key, value?.join?.(","));
            } else if (
                key === "start_at" ||
                key === "end_at" ||
                (typeof value === "string"
                    ? value.trim().length > 0
                    : value?.length > 0)
            ) {
                apiFilters.set(key, value);
            }
        });
        setSearchParams(apiFilters);
    });

    const onClearFilter = () => {
        methods.reset();
        setDateRange("");

        const columns = params.get("columns");
        const search = params.get("search");
        const phone = params.get("phone");

        const queryParams = new URLSearchParams();

        if (columns) queryParams.set("columns", columns);
        if (search) queryParams.set("search", search);
        if (phone) queryParams.set("phone", phone);

        const queryString = queryParams.toString();
        const url = queryString
            ? `${location.pathname}?${queryString}`
            : location.pathname;

        navigate(url);
    };

    const onDateRangeChange = (_, { value }) => {
        setDateRange(value);
    };

    return (
        <Grid className="CallHistoryToolbar">
            <CustomRangeDateModal
                open={isCustomDateRangeOpen}
                onDateRangeChange={onDateRangeChange}
                dateRange={dateRange}
                getData={() => setCustomDateRangeOpen(false)}
                confirmButtonLabel="Select"
                onClose={() => setCustomDateRangeOpen(false)}
                applyDisabled={
                    !dateRange?.split?.(" - ")?.[0] ||
                    !dateRange?.split?.(" - ")?.[1]
                }
            />
            <Grid.Row>
                <Grid.Column>
                    <FormProvider {...methods}>
                        <Form onSubmit={onSubmit}>
                            <Segment.Group>
                                <Segment size="mini" padded>
                                    <DateRangePowerSelector
                                        label="Date"
                                        name="date"
                                        onCustomSelect={() =>
                                            setCustomDateRangeOpen(true)
                                        }
                                    />
                                    <PowerSelector
                                        multiple
                                        label="User"
                                        name="user_id"
                                        options={userOptions}
                                    />
                                    <PowerSelector
                                        multiple
                                        label="Direction"
                                        name="direction"
                                        options={PhoneUtil.DIRECTION_OPTIONS}
                                    />
                                    <PowerSelector
                                        multiple
                                        label="Call Type"
                                        name="call_type"
                                        options={PhoneUtil.CALL_TYPE_OPTIONS}
                                    />
                                    <PowerSelector
                                        multiple
                                        label="Destination"
                                        name="destination"
                                        options={destinationsOptions}
                                        staggeredCheckbox
                                    />
                                    <PowerSelector
                                        multiple
                                        label="System Result"
                                        name={"system_result"}
                                        options={
                                            PhoneUtil.SYSTEM_RESULT_OPTIONS
                                        }
                                    />
                                    <PowerSelector
                                        multiple
                                        label="User Result"
                                        name="user_result"
                                        options={PhoneUtil.CALL_RESULT_OPTIONS}
                                    />
                                    <PowerSelector
                                        label="Call Tags"
                                        name="tags"
                                        placeholder="Search call tags"
                                        options={tagsCategoryOptions}
                                        className="Tags_Dropdown"
                                        staggeredCheckbox
                                        multiple
                                    />
                                    <PowerSelector
                                        multiple
                                        label="Voicemail"
                                        name="voicemail_listened"
                                        options={PhoneUtil.VOICEMAIL_OPTIONS}
                                    />
                                </Segment>
                                <Segment textAlign="right">
                                    <Button
                                        onClick={onClearFilter}
                                        color="primary"
                                        basic
                                        disabled={
                                            !PhoneUtil.CALL_HISTORY_FILTER_PARAMS.some(
                                                (param) => params.get(param)
                                            )
                                        }
                                    >
                                        Clear Filters
                                    </Button>
                                    <Divider vertical />
                                    <Button
                                        disabled={!isValid || !isDirty}
                                        color="primary"
                                        type="submit"
                                    >
                                        Apply
                                    </Button>
                                </Segment>
                            </Segment.Group>
                        </Form>
                    </FormProvider>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row verticalAlign="middle">
                <Grid.Column>
                    <ModulesFilter
                        isLoading={
                            isCallHistoryLoading &&
                            (params?.get("phone") || params.get("search"))
                        }
                        search={searchValue}
                        cancelFunction={cancelFunction}
                        onSearchSubmit={onSearchSubmit}
                        onChangeSearch={onSearch}
                        columnRows={COLUMN_ROWS}
                        columnCount={3}
                        isColumnHidden={isColumnHidden}
                        onToggleColumn={onToggleColumn}
                        searchBarPlaceholder="Search by contact name, account name or phone number"
                    />
                </Grid.Column>
                <Grid.Column />
            </Grid.Row>
        </Grid>
    );
};

export default CallHistoryToolbar;
